import { cn } from '@/lib/utils'
import { Empresa } from '@/service/api'
import { PeriodoPontoFactory, Void } from '@acme/common'
import { Box, BoxProps, Typography } from '@mui/material'
import BntGroup, { ButtonGroupProps } from '@mui/material/ButtonGroup'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import {
    DateCalendar,
    DatePicker,
    DatePickerProps,
    LocalizationProvider,
    MonthCalendar,
} from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs, { Dayjs } from 'dayjs'
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react'
import React, { createContext, useEffect, useState } from 'react'
import { toast } from 'sonner'
import { useStore } from 'store'
import { match } from 'ts-pattern'
import { Button } from '../Button'
import Menu from '../MenuV2'

type TValue = [string, string]

type Tipo = 'competencia' | 'data' | 'periodo'
export type { Tipo as TipoPeriodo }

const Context = createContext({
    state: [
        dayjs().format('YYYY-MM-DD'),
        dayjs().format('YYYY-MM-DD'),
    ] as TValue,
    allowedTypes: ['data'] as Tipo[],
    type: 'data' as Tipo,
    changeType: (t: Tipo) => {
        console.debug('changeType', t)
    },
    size: 'small' as 'small' | 'medium' | undefined,
    label: 'Período' as string | undefined,
    variant: 'filled' as 'filled' | 'outlined' | undefined,
    handlePrev: Void,
    handleNext: Void,
    handleChange: Void as IRootProps['onChange'],
    empresa: null as Empresa | null,
})

interface IRootProps {
    size?: 'small' | 'medium'
    label?: string
    variant?: 'filled' | 'outlined'
    allowedTypes?: Tipo[]
    value: TValue
    onChange: React.Dispatch<React.SetStateAction<TValue>>
    children: React.ReactNode
    /** com botões: `320`, sem botões: `220`
     * @default 220
     */
    width?: number
    type?: Tipo
    setType?: React.Dispatch<React.SetStateAction<Tipo>>
}
type RootProps = { BoxProps?: Omit<BoxProps, keyof IRootProps> } & IRootProps

function Root({
    size = 'small',
    label,
    variant = 'filled',
    onChange,
    value,
    allowedTypes = ['competencia'],
    children,
    width = 220,
    BoxProps,
    type: _type,
    setType: _setType,
}: RootProps) {
    const [typeDisplayed, setTypeDisplayed] = useState<Tipo>(allowedTypes[0])
    const empresa = useStore(state => state.company)
    const td = _type ?? typeDisplayed
    const setTd = _setType ?? setTypeDisplayed

    const setStore = useStore(state => state.setCompetence)

    useEffect(() => {
        if (!empresa) return
        if (td === 'competencia') {
            const [competencia] = PeriodoPontoFactory.fromPeriodo(
                ...value,
            ).getCompetencias({
                diaInicioPonto: empresa.diaInicioPonto,
                trocasInicioPonto: empresa.TrocaInicioPonto,
            })
            setStore(competencia)
        }
    }, [value[1]])

    const handleNext = React.useCallback(() => {
        if (!empresa) return
        switch (td) {
            case 'competencia':
                onChange(([oldInicial, oldFinal]) => {
                    const [competencia] = PeriodoPontoFactory.fromPeriodo(
                        oldInicial,
                        oldFinal,
                    ).getCompetencias({
                        diaInicioPonto: empresa.diaInicioPonto,
                        trocasInicioPonto: empresa.TrocaInicioPonto,
                    })
                    const competenciaProxima = dayjs(competencia)
                        .add(1, 'month')
                        .format('YYYY-MM')
                    const [dataInicial, dataFinal] =
                        PeriodoPontoFactory.fromCompetencia(
                            competenciaProxima,
                            {
                                diaInicioPonto: empresa.diaInicioPonto,
                                trocasInicioPonto: empresa.TrocaInicioPonto,
                            },
                        ).getPeriodo()
                    return [
                        dataInicial.format('YYYY-MM-DD'),
                        dataFinal.format('YYYY-MM-DD'),
                    ]
                })
                break
            case 'data':
                onChange(([oldInicial]) => {
                    const diaProximo = dayjs(oldInicial)
                        .add(1, 'day')
                        .format('YYYY-MM-DD')
                    return [diaProximo, diaProximo]
                })
                break
            case 'periodo':
                onChange(([oldInicial, oldFinal]) => {
                    const diaProximoInicial = dayjs(oldInicial)
                        .add(1, 'day')
                        .format('YYYY-MM-DD')
                    const diaProximoFinal = dayjs(oldFinal)
                        .add(1, 'day')
                        .format('YYYY-MM-DD')
                    return [diaProximoInicial, diaProximoFinal]
                })
                break
        }
    }, [value, onChange, empresa])

    const handlePrev = React.useCallback(() => {
        if (!empresa) return
        switch (td) {
            case 'competencia':
                onChange(([oldInicial, oldFinal]) => {
                    const [competencia] = PeriodoPontoFactory.fromPeriodo(
                        oldInicial,
                        oldFinal,
                    ).getCompetencias({
                        diaInicioPonto: empresa.diaInicioPonto,
                        trocasInicioPonto: empresa.TrocaInicioPonto,
                    })
                    const competenciaAnterior = dayjs(competencia)
                        .subtract(1, 'month')
                        .format('YYYY-MM')
                    const [dataInicial, dataFinal] =
                        PeriodoPontoFactory.fromCompetencia(
                            competenciaAnterior,
                            {
                                diaInicioPonto: empresa.diaInicioPonto,
                                trocasInicioPonto: empresa.TrocaInicioPonto,
                            },
                        ).getPeriodo()
                    return [
                        dataInicial.format('YYYY-MM-DD'),
                        dataFinal.format('YYYY-MM-DD'),
                    ]
                })
                break
            case 'data':
                onChange(([oldInicial]) => {
                    const diaAnterior = dayjs(oldInicial)
                        .subtract(1, 'day')
                        .format('YYYY-MM-DD')
                    return [diaAnterior, diaAnterior]
                })
                break
            case 'periodo':
                onChange(([oldInicial, oldFinal]) => {
                    const diaAnteriorInicial = dayjs(oldInicial)
                        .subtract(1, 'day')
                        .format('YYYY-MM-DD')
                    const diaAnteriorFinal = dayjs(oldFinal)
                        .subtract(1, 'day')
                        .format('YYYY-MM-DD')
                    return [diaAnteriorInicial, diaAnteriorFinal]
                })
                break
        }
    }, [value, onChange])

    return (
        <Context.Provider
            value={{
                state: value,
                handlePrev,
                handleNext,
                handleChange: onChange,
                size,
                label,
                variant,
                type: td,
                changeType: setTd,
                allowedTypes,
                empresa,
            }}
        >
            <Box
                width={width ?? 'fit-Content'}
                display="flex"
                gap={1}
                alignItems="center"
                {...BoxProps}
            >
                {children}
            </Box>
        </Context.Provider>
    )
}

const sizePattern = (size?: 'small' | 'medium') =>
    match(size)
        .with('small', () => 'h-[48px]' as const)
        .otherwise(() => 'h-[55px]' as const)

function DateSelector({
    sx,
    readOnly,
    ...props
}: DatePickerProps<dayjs.Dayjs>) {
    function tabProps(index: number) {
        return {
            id: `simple-tab-${index}`,
            'aria-controls': `simple-tabpanel-${index}`,
        }
    }

    function getIndex(type: Tipo) {
        switch (type) {
            case 'competencia':
                return 0
            case 'data':
                return 1
            case 'periodo':
                return 2
        }
    }

    function getTipo(index: number) {
        switch (index) {
            case 0:
                return 'competencia'
            case 1:
                return 'data'
            case 2:
                return 'periodo'
        }
    }

    return (
        <Context.Consumer>
            {({
                state: [dataInicial, dataFinal],
                variant,
                label,
                size,
                type,
                changeType,
                empresa,
                handleChange,
                allowedTypes,
            }) => (
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    {(() => {
                        const Picker = () => (
                            <div
                                className={cn(
                                    sizePattern(size),
                                    'hover:bg-card/20',
                                    'transition-all duration-200',
                                    'cursor-pointer py-1 overflow-hidden items-center flex rounded-t-sm',
                                    props.disabled || readOnly
                                        ? 'pointer-events-none cursor-not-allowed'
                                        : '',
                                    variant === 'filled'
                                        ? 'bg-[rgba(0,_0,_0,_0.05)] *:-mt-2'
                                        : 'rounded-sm *:mt-1.5',
                                )}
                                style={sx as any}
                            >
                                <DatePicker
                                    {...props}
                                    slotProps={{
                                        ...props.slotProps,
                                        textField: {
                                            variant,
                                            size,
                                            label:
                                                label ||
                                                match(type)
                                                    .with(
                                                        'competencia',
                                                        () => 'Competência',
                                                    )
                                                    .with(
                                                        'periodo',
                                                        () => 'Período',
                                                    )
                                                    .with('data', () => 'Data')
                                                    .exhaustive(),
                                            ...props.slotProps?.textField,
                                        },
                                    }}
                                    format={match(type)
                                        .with('data', () => 'DD/MM/YYYY (ddd)')
                                        .with(
                                            'competencia',
                                            () => 'MM/YYYY (MMMM)',
                                        )
                                        .with(
                                            'periodo',
                                            () =>
                                                `[${dayjs(dataInicial).format(
                                                    'DD/MM/YY',
                                                )}] [a] DD/MM/YY`,
                                        )
                                        .exhaustive()}
                                    value={dayjs(dataFinal)}
                                    readOnly
                                    sx={{
                                        '& .MuiFormLabel-root': {
                                            cursor: readOnly
                                                ? 'auto'
                                                : 'pointer',
                                            textTransform: 'capitalize',
                                        },
                                    }}
                                    className="pointer-events-none size-full p-0"
                                />
                            </div>
                        )

                        if (readOnly) return Picker()
                        return (
                            <Menu.Root>
                                <Menu.Trigger>{Picker()}</Menu.Trigger>
                                <Menu.List
                                    onClick={Void}
                                    sx={{
                                        '& .MuiList-root': {
                                            py: 0,
                                            width: 'fit-content',
                                        },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            borderBottom: 1,
                                            borderColor: 'divider',
                                            height: '100%',
                                        }}
                                    >
                                        {allowedTypes.length > 1 && (
                                            <Tabs
                                                value={getIndex(type)}
                                                onChange={(_, v: number) => {
                                                    if (!empresa) return
                                                    const tipo = getTipo(v)!
                                                    switch (tipo) {
                                                        case 'competencia':
                                                        case 'periodo':
                                                            const [
                                                                diaInicial,
                                                                diaFinal,
                                                            ] =
                                                                PeriodoPontoFactory.fromCompetencia(
                                                                    dayjs(
                                                                        dataFinal,
                                                                    ).format(
                                                                        'YYYY-MM',
                                                                    ),
                                                                    {
                                                                        diaInicioPonto:
                                                                            empresa.diaInicioPonto,
                                                                        trocasInicioPonto:
                                                                            empresa.TrocaInicioPonto,
                                                                    },
                                                                ).getPeriodo(
                                                                    false,
                                                                )
                                                            handleChange([
                                                                diaInicial.format(
                                                                    'YYYY-MM-DD',
                                                                ),
                                                                diaFinal.format(
                                                                    'YYYY-MM-DD',
                                                                ),
                                                            ])
                                                            break
                                                        case 'data':
                                                            const dia =
                                                                dayjs().isBefore(
                                                                    dataFinal,
                                                                )
                                                                    ? dayjs().format(
                                                                          'YYYY-MM-DD',
                                                                      )
                                                                    : dataFinal
                                                            handleChange([
                                                                dia,
                                                                dia,
                                                            ])
                                                            break
                                                    }
                                                    changeType(getTipo(v)!)
                                                }}
                                                centered
                                                variant="fullWidth"
                                                sx={{ px: 1 }}
                                            >
                                                <Tab
                                                    label="Competência"
                                                    {...tabProps(0)}
                                                    disabled={
                                                        !allowedTypes.includes(
                                                            'competencia',
                                                        )
                                                    }
                                                    sx={
                                                        !allowedTypes.includes(
                                                            'competencia',
                                                        )
                                                            ? {
                                                                  display:
                                                                      'none',
                                                              }
                                                            : {}
                                                    }
                                                />
                                                <Tab
                                                    label="Data"
                                                    {...tabProps(1)}
                                                    disabled={
                                                        !allowedTypes.includes(
                                                            'data',
                                                        )
                                                    }
                                                    sx={
                                                        !allowedTypes.includes(
                                                            'data',
                                                        )
                                                            ? {
                                                                  display:
                                                                      'none',
                                                              }
                                                            : {}
                                                    }
                                                />
                                                <Tab
                                                    label="Período"
                                                    {...tabProps(2)}
                                                    disabled={
                                                        !allowedTypes.includes(
                                                            'periodo',
                                                        )
                                                    }
                                                    sx={
                                                        !allowedTypes.includes(
                                                            'periodo',
                                                        )
                                                            ? {
                                                                  display:
                                                                      'none',
                                                              }
                                                            : {}
                                                    }
                                                />
                                            </Tabs>
                                        )}
                                    </Box>
                                    <CustomTabPanel
                                        value={getIndex(type)}
                                        index={0}
                                    >
                                        <SelectCompetencia />
                                    </CustomTabPanel>
                                    <CustomTabPanel
                                        value={getIndex(type)}
                                        index={1}
                                    >
                                        <SelectData />
                                    </CustomTabPanel>
                                    <CustomTabPanel
                                        value={getIndex(type)}
                                        index={2}
                                    >
                                        <SelectPeriodo />
                                    </CustomTabPanel>
                                </Menu.List>
                            </Menu.Root>
                        )
                    })()}
                </LocalizationProvider>
            )}
        </Context.Consumer>
    )
}

interface ITabPanelProps {
    children?: React.ReactNode
    index: number
    value: number
}

function CustomTabPanel({ children, value, index, ...other }: ITabPanelProps) {
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            style={{ width: '100%', height: '100%' }}
            {...other}
        >
            {value === index && children}
        </div>
    )
}

function SelectCompetencia() {
    return (
        <Context.Consumer>
            {({ handleChange, empresa, state: [dataInicial, dataFinal] }) => (
                <Box sx={{ width: '100%', height: '100%' }}>
                    <Box
                        sx={{
                            height: 'fit-content',
                            width: '100%',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            px: 1,
                            my: 1,
                            display: 'inline-flex',
                        }}
                    >
                        <Button
                            onClick={() => {
                                if (!empresa) return
                                const data = dayjs(dataFinal)
                                const competencia = data
                                    .set('year', data.year() - 1)
                                    .set('month', 11)
                                    .format('YYYY-MM')
                                const [diaInicial, diaFinal] =
                                    PeriodoPontoFactory.fromCompetencia(
                                        competencia,
                                        {
                                            diaInicioPonto:
                                                empresa.diaInicioPonto,
                                            trocasInicioPonto:
                                                empresa.TrocaInicioPonto,
                                        },
                                    ).getPeriodo()
                                handleChange([
                                    diaInicial.format('YYYY-MM-DD'),
                                    diaFinal.format('YYYY-MM-DD'),
                                ] as TValue)
                            }}
                            sx={{
                                width: 45,
                                height: 45,
                            }}
                            title="Ano anterior"
                            variant="outlined"
                        >
                            <div className="inline-flex items-center">
                                <ChevronLeftIcon />
                            </div>
                        </Button>
                        <Typography variant="h5" sx={{ userSelect: 'none' }}>
                            {dayjs(dataFinal).format('YYYY')}
                        </Typography>
                        <Button
                            onClick={() => {
                                if (!empresa) return
                                const data = dayjs(dataFinal)
                                const competencia = data
                                    .set('year', data.year() + 1)
                                    .set('month', 0)
                                    .format('YYYY-MM')
                                const [diaInicial, diaFinal] =
                                    PeriodoPontoFactory.fromCompetencia(
                                        competencia,
                                        {
                                            diaInicioPonto:
                                                empresa.diaInicioPonto,
                                            trocasInicioPonto:
                                                empresa.TrocaInicioPonto,
                                        },
                                    ).getPeriodo()
                                handleChange([
                                    diaInicial.format('YYYY-MM-DD'),
                                    diaFinal.format('YYYY-MM-DD'),
                                ] as TValue)
                            }}
                            sx={{
                                width: 45,
                                height: 45,
                            }}
                            title="Próximo ano"
                            variant="outlined"
                        >
                            <div className="inline-flex items-center">
                                <ChevronRightIcon />
                            </div>
                        </Button>
                    </Box>
                    <MonthCalendar
                        onChange={(competencia: Dayjs) => {
                            if (!empresa) return
                            const [diaInicial, diaFinal] =
                                PeriodoPontoFactory.fromCompetencia(
                                    competencia.format('YYYY-MM'),
                                    {
                                        diaInicioPonto: empresa.diaInicioPonto,
                                        trocasInicioPonto:
                                            empresa.TrocaInicioPonto,
                                    },
                                ).getPeriodo()
                            handleChange([
                                diaInicial.format('YYYY-MM-DD'),
                                diaFinal.format('YYYY-MM-DD'),
                            ] as TValue)
                        }}
                        value={dayjs(dataFinal)}
                    />
                    <Typography
                        sx={{ textAlign: 'center', py: 2, userSelect: 'none' }}
                    >
                        Período de{' '}
                        <strong>
                            {dayjs(dataInicial).format('DD/MM/YYYY')}
                        </strong>{' '}
                        a{' '}
                        <strong>{dayjs(dataFinal).format('DD/MM/YYYY')}</strong>
                    </Typography>
                </Box>
            )}
        </Context.Consumer>
    )
}

function SelectData() {
    return (
        <Context.Consumer>
            {({ handleChange, state: [, dataSelecionada] }) => (
                <Box sx={{ width: '100%', height: '100%' }}>
                    <DateCalendar
                        onChange={(data: Dayjs | null) => {
                            const dia = dayjs(data || undefined).format(
                                'YYYY-MM-DD',
                            )
                            handleChange([dia, dia] as TValue)
                        }}
                        value={dayjs(dataSelecionada)}
                    />
                </Box>
            )}
        </Context.Consumer>
    )
}

function SelectPeriodo() {
    return (
        <Context.Consumer>
            {({ handleChange, state: [dataInicial, dataFinal] }) => (
                <Box
                    sx={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        px: 1,
                        py: 2,
                    }}
                >
                    <Box
                        sx={{
                            display: 'inline-flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 2,
                        }}
                    >
                        <Typography
                            variant="h6"
                            sx={{ userSelect: 'none', mr: 1 }}
                        >
                            De&nbsp;
                        </Typography>
                        <DatePicker
                            value={dayjs(dataInicial)}
                            format="DD/MM/YYYY"
                            sx={{ width: 150, mr: 1 }}
                            onChange={(data: Dayjs | null) => {
                                const dia = dayjs(data || undefined).format(
                                    'YYYY-MM-DD',
                                )
                                handleChange([dia, dataFinal] as TValue)
                            }}
                        />
                        <BntGroup sx={{ height: 'auto' }}>
                            <Button
                                onClick={() => {
                                    const diaAnterior = dayjs(dataInicial)
                                        .subtract(1, 'day')
                                        .format('YYYY-MM-DD')
                                    handleChange([
                                        diaAnterior,
                                        dataFinal,
                                    ] as TValue)
                                }}
                                sx={{
                                    width: 55,
                                    height: 55,
                                }}
                                title="Dia anterior"
                                variant="outlined"
                            >
                                <div className="inline-flex items-center">
                                    <ChevronLeftIcon />
                                </div>
                            </Button>
                            <Button
                                onClick={() => {
                                    const diaProximo = dayjs(dataInicial)
                                        .add(1, 'day')
                                        .format('YYYY-MM-DD')
                                    handleChange([
                                        diaProximo,
                                        dataFinal,
                                    ] as TValue)
                                }}
                                sx={{
                                    width: 55,
                                    height: 55,
                                }}
                                title="Próximo dia"
                                variant="outlined"
                            >
                                <div className="inline-flex items-center">
                                    <ChevronRightIcon />
                                </div>
                            </Button>
                        </BntGroup>
                    </Box>
                    <Box
                        sx={{
                            display: 'inline-flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                    >
                        <Typography
                            variant="h6"
                            sx={{ userSelect: 'none', mr: 1 }}
                        >
                            Até
                        </Typography>
                        <DatePicker
                            value={dayjs(dataFinal)}
                            format="DD/MM/YYYY"
                            sx={{ width: 150, mr: 1 }}
                            onChange={(data: Dayjs | null) => {
                                const dia = dayjs(data || undefined).format(
                                    'YYYY-MM-DD',
                                )
                                handleChange([dataInicial, dia] as TValue)
                            }}
                        />
                        <BntGroup sx={{ height: 'auto' }}>
                            <Button
                                onClick={() => {
                                    const diaAnterior = dayjs(dataFinal)
                                        .subtract(1, 'day')
                                        .format('YYYY-MM-DD')
                                    handleChange([
                                        dataInicial,
                                        diaAnterior,
                                    ] as TValue)
                                }}
                                sx={{
                                    width: 55,
                                    height: 55,
                                }}
                                title="Dia anterior"
                                variant="outlined"
                            >
                                <div className="inline-flex items-center">
                                    <ChevronLeftIcon />
                                </div>
                            </Button>
                            <Button
                                onClick={() => {
                                    const diaProximo = dayjs(dataFinal)
                                        .add(1, 'day')
                                        .format('YYYY-MM-DD')
                                    handleChange([
                                        dataInicial,
                                        diaProximo,
                                    ] as TValue)
                                }}
                                sx={{
                                    width: 55,
                                    height: 55,
                                }}
                                title="Próximo dia"
                                variant="outlined"
                            >
                                <div className="inline-flex items-center">
                                    <ChevronRightIcon />
                                </div>
                            </Button>
                        </BntGroup>
                    </Box>
                </Box>
            )}
        </Context.Consumer>
    )
}

function Buttons(props: ButtonGroupProps) {
    return (
        <Context.Consumer>
            {({ handlePrev, handleNext, size, variant }) => (
                <BntGroup
                    {...props}
                    className={cn(
                        sizePattern(size),
                        'flex flex-row overflow-hidden items-center *:h-full w-fit [&_button]:border [&_button]:w-12! [&_button]:p-4',
                    )}
                >
                    <Button
                        onClick={handlePrev}
                        title="Data anterior"
                        variant={match(variant)
                            .with('filled', () => 'text' as const)
                            .otherwise(() => 'outlined' as const)}
                    >
                        <div className="inline-flex items-center">
                            <ChevronLeftIcon />
                        </div>
                    </Button>
                    <Button
                        onClick={handleNext}
                        title="Próxima data"
                        variant={match(variant)
                            .with('filled', () => 'text' as const)
                            .otherwise(() => 'outlined' as const)}
                    >
                        <div className="inline-flex items-center">
                            <ChevronRightIcon />
                        </div>
                    </Button>
                </BntGroup>
            )}
        </Context.Consumer>
    )
}

interface InputPeriodo {
    /**
     * Tipo de período no render inicial,
     * deve ser o index 0 passado ao allowedTypes do SP.Root.
     * Pode ser ignorado caso defaultValue for informado
     * @default 'competencia'
     * */
    type: Tipo
    /**
     * Quantidade máxima de dias permitidos no período
     * @default 366
     * */
    periodoMax: number
    /** Valor inicial do hook */
    defaultValue?: TValue
}

/**
 * Hook para gerenciar o período de datas, controla datas iniciais e finais
 * @param type Tipo de período no render inicial, deve ser o index 0 passado ao allowedTypes do SP.Root
 * @param periodoMax Quantidade máxima de dias permitidos no período
 * @example const [[dataInicial, dataFinal], setPeriodo] = usePeriodo()
 */
function usePeriodo(
    {
        type = 'competencia',
        periodoMax = 366,
        defaultValue = ['', ''],
    }: Partial<InputPeriodo> = {
        type: 'competencia',
        periodoMax: 366,
    },
) {
    const empresa = useStore(state => state.company)
    const competenciaAberta = empresa?.competenciaAtual

    let defaultInicial: string = defaultValue?.[0] || ''
    let defaultFinal: string = defaultValue?.[1] || ''

    const wasPreloadedRef = React.useRef(false)

    function getDefaultValues() {
        if (!empresa) {
            if (type === 'data') {
                const hoje = dayjs().format('YYYY-MM-DD')
                return [hoje, hoje]
            }
            return [
                dayjs().startOf('month').format('YYYY-MM-DD'),
                dayjs().endOf('month').format('YYYY-MM-DD'),
            ]
        }

        let defaultInicialLocal = defaultValue?.[0],
            defaultFinalLocal = defaultValue?.[1]

        switch (type) {
            case 'competencia':
            case 'periodo':
                const [dataInicial, dataFinal] =
                    PeriodoPontoFactory.fromCompetencia(
                        dayjs(competenciaAberta).format('YYYY-MM'),
                        {
                            diaInicioPonto: empresa.diaInicioPonto,
                            trocasInicioPonto: empresa.TrocaInicioPonto,
                        },
                    ).getPeriodo(true)
                defaultInicialLocal = dataInicial
                defaultFinalLocal = dataFinal
                break
            case 'data':
                const hoje = dayjs().format('YYYY-MM-DD')
                defaultInicialLocal = hoje
                defaultFinalLocal = hoje
                break
        }

        return [defaultInicialLocal, defaultFinalLocal]
    }

    if (!defaultInicial || !defaultFinal) {
        if (!empresa) wasPreloadedRef.current = true
        ;[defaultInicial, defaultFinal] = getDefaultValues()
    }

    const [[dataInicial, dataFinal], _setPeriodo] = useState<TValue>([
        defaultInicial,
        defaultFinal,
    ])

    function setPeriodo(value: TValue | ((prev: TValue) => TValue) | null) {
        if (value === null) return _setPeriodo([defaultInicial, defaultFinal])

        _setPeriodo(prev => {
            const newValue = typeof value === 'function' ? value(prev) : value
            if (Array.isArray(newValue) && newValue.length === 2) {
                const [start, end] = newValue
                if (dayjs.utc(end).diff(dayjs.utc(start), 'day') > periodoMax) {
                    toast.error(
                        `O período não pode ser superior a ${periodoMax} dias`,
                    )
                    return prev
                }
            }
            return newValue
        })
    }

    useEffect(() => {
        if (wasPreloadedRef.current && empresa) {
            wasPreloadedRef.current = false
            setPeriodo(getDefaultValues() as TValue)
            return
        }

        if (empresa && type == 'competencia') {
            const [competencia] = PeriodoPontoFactory.fromPeriodo(
                dataInicial,
                dataFinal,
            ).getCompetencias({
                diaInicioPonto: empresa.diaInicioPonto,
                trocasInicioPonto: empresa.TrocaInicioPonto,
            })
            if (
                empresa.competenciaAtual &&
                competencia != empresa.competenciaAtual
            ) {
                const [diaInicial, diaFinal] =
                    PeriodoPontoFactory.fromCompetencia(
                        dayjs(empresa.competenciaAtual).format('YYYY-MM'),
                        {
                            diaInicioPonto: empresa.diaInicioPonto,
                            trocasInicioPonto: empresa.TrocaInicioPonto,
                        },
                    ).getPeriodo(true)
                setPeriodo([diaInicial, diaFinal])
            }
        }
    }, [empresa?.id])

    return [[dataInicial, dataFinal], setPeriodo] as const
}

export { usePeriodo }

/**
 * @example
 *  import SP, { usePeriodo } from 'global/components/Selects/SelectPeriodo'
 *
 *  const [[dataInicial, dataFinal], setPeriodo] = usePeriodo('competencia')
 *
 *  <SP.Root
 *      onChange={setPeriodo}
 *      value={[dataInicial, dataFinal]}
 *      allowedTypes={['competencia', 'periodo', 'data']}
 *      variant="filled"
 *      size="small"
 *      width={220}
 *  >
 *      <SP.DateSelector />
 *      <SP.Buttons />
 *  </SP.Root>
 */
export default {
    Root,
    DateSelector,
    Buttons,
}
